import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { GUIDE_MODES } from "./guideModes";
import {
  LayoutGrid, Palette, ClipboardList, Lightbulb, Library, Clock, FolderOpen, Trash2, Lock, Plus, MessageSquare, FileText,
} from "lucide-react";

const CONNECTED = [
  { label: "Content Command Center", desc: "Theme Library & Reuse Theme live here", to: "/content", icon: LayoutGrid },
  { label: "Brand Center", desc: "Your voice, audience & standards", to: "/brand", icon: Palette },
  { label: "Idea Vault", desc: "Captured ideas & seeds", to: "/ideas", icon: Lightbulb },
  { label: "SOP Library", desc: "Your repeatable systems", to: "/sops", icon: ClipboardList },
];

export default function GuideHub({ conversations, projects, onOpenMode, onOpenConversation, onOpenProject, onViewAllProjects, onNewProject, onDeleteConversation }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-10" data-testid="guide-hub">
      {/* Guide workspaces */}
      <section>
        <h2 className="font-heading text-lg mb-4 text-foreground/90">Guide workspaces</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GUIDE_MODES.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.key} onClick={() => onOpenMode(m.key)} data-testid={`guide-open-${m.key}`} className="text-left group">
                <Card className="rounded-3xl border-border shadow-soft p-5 h-full hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-300">
                  <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <p className="font-heading text-lg mb-1">{m.label}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.blurb}</p>
                </Card>
              </button>
            );
          })}
        </div>
      </section>

      {/* Connected systems */}
      <section>
        <h2 className="font-heading text-lg mb-4 text-foreground/90">Connected systems</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONNECTED.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.to} onClick={() => navigate(c.to)} data-testid={`guide-connected-${c.to.slice(1)}`} className="text-left">
                <Card className="rounded-3xl border-border shadow-soft p-5 h-full hover:shadow-soft-lg transition-all duration-300 flex items-start gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-secondary/15 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-secondary" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-medium">{c.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.desc}</p>
                  </div>
                </Card>
              </button>
            );
          })}
          <button onClick={() => navigate("/knowledge")} data-testid="guide-connected-knowledge" className="text-left">
            <Card className="rounded-3xl border-border shadow-soft p-5 h-full hover:shadow-soft-lg transition-all duration-300 flex items-start gap-3">
              <div className="h-10 w-10 rounded-2xl bg-secondary/15 flex items-center justify-center shrink-0">
                <Library className="h-5 w-5 text-secondary" strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-medium">Knowledge Library</p>
                <p className="text-xs text-muted-foreground mt-0.5">Upload PDFs, docs & guides for the Guide to reference long-term.</p>
              </div>
            </Card>
          </button>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent conversations */}
        <section>
          <h2 className="font-heading text-lg mb-4 text-foreground/90 flex items-center gap-2"><Clock className="h-4 w-4 text-accent" /> Recent conversations</h2>
          <Card className="rounded-3xl border-border shadow-soft p-3" data-testid="guide-conversations-list">
            {conversations.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-6 text-center">No conversations yet. Open a workspace to begin.</p>
            ) : (
              <div className="space-y-1">
                {conversations.slice(0, 8).map((c) => (
                  <div key={c.id} className="flex items-center gap-2 group">
                    <button onClick={() => onOpenConversation(c)} data-testid={`guide-conversation-${c.id}`} className="flex-1 text-left px-3 py-2.5 rounded-2xl hover:bg-muted transition-colors">
                      <p className="text-sm font-medium truncate">{c.title || "Conversation"}</p>
                      <p className="text-xs text-muted-foreground capitalize">{(c.mode || "ask").replace("_", " ")}</p>
                    </button>
                    <button onClick={() => onDeleteConversation(c.id)} data-testid={`guide-delete-conversation-${c.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </section>

        {/* Your projects */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-foreground/90 flex items-center gap-2"><FolderOpen className="h-4 w-4 text-accent" /> Your projects</h2>
            <div className="flex items-center gap-2">
              <button onClick={onNewProject} data-testid="guide-new-project-hub" className="text-xs flex items-center gap-1 text-secondary hover:text-secondary/80 transition-colors"><Plus className="h-3.5 w-3.5" /> New</button>
              <button onClick={onViewAllProjects} data-testid="guide-view-all-projects" className="text-xs text-muted-foreground hover:text-foreground transition-colors">View all →</button>
            </div>
          </div>
          <Card className="rounded-3xl border-border shadow-soft p-3" data-testid="guide-projects-list">
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-6 text-center">No projects yet. Organize active work like content series, products &amp; books here.</p>
            ) : (
              <div className="space-y-1">
                {projects.slice(0, 8).map((p) => (
                  <button key={p.id} onClick={() => onOpenProject(p)} data-testid={`guide-project-${p.id}`} className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-2xl hover:bg-muted transition-colors">
                    <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><FolderOpen className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{p.title || "Untitled"}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.category}</p>
                    </div>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                      <span className="inline-flex items-center gap-0.5"><MessageSquare className="h-3 w-3" />{p.conversation_count ?? 0}</span>
                      <span className="inline-flex items-center gap-0.5"><FileText className="h-3 w-3" />{p.item_count ?? 0}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </section>
      </div>
    </div>
  );
}
